import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from "rxjs";
import {Constant} from "../../common/constant/constant";

@Injectable({
    providedIn: 'root'
})
export class AllDataApiService {

    private httpHeaders: HttpHeaders;
    private accessToken = null;
    private allDataAccess: any = {};

    private expireTime: Date = null;

    constructor(private httpClient: HttpClient) {
        this.refreshAccessToken();
    }

    allDataSearch(apiFormData): Observable<any> {
        // Truncate query if it exceeds 50 characters
        if (apiFormData?.query?.length > 50) {
            apiFormData.query = apiFormData.query.substring(0, 50);
        }
        return this.httpClient.post(`${Constant.collabticV3ApiUrl}/all-data/search`, apiFormData, {headers: this.httpHeaders});
    }

    filterDataByReportId(reportIdParam: number, isNewVersion: boolean, showAllData: boolean): Observable<any> {
        return this.httpClient.post(`${Constant.collabticV3ApiUrl}/all-data/filter`, {
            reportId: reportIdParam,
            apikey: 'dG9wZml4MTIz',
            userId: localStorage.getItem('userId'),
            isNewVersion: isNewVersion,
            showAllData: showAllData
        }, {headers: this.httpHeaders});
    }


    // handle the all data redirection
    handleAllDataRedirection() {
        try {
            // Get the full URL from `window.location.href`
            const url = new URL(window.location.href);

            // Create a URLSearchParams object
            const params = new URLSearchParams(url.search);

            // Get specific parameters
            const code = params.get('code');
            const stateUrl = params.get('state');

            if (code && stateUrl && stateUrl.includes('http')) {
                // redirect it to requested client url if stateUrl is present
                window.location.href = `${stateUrl}?code=${code}`;
            } else {
                // get access token if stateUrl is not present
                this.getAccessTokenByUserId(localStorage.getItem('userId')).subscribe((tokenResponse) => {
                        // @ts-ignore
                        this.accessToken = tokenResponse?.access_token;
                        localStorage.setItem('all-data-access', JSON.stringify(tokenResponse));
                        this.refreshAccessToken();
                    },
                    (error) => {
                        console.error('Error getting access token', error);
                    }
                );
            }
        } catch (e) {
            console.error('Error in all data redirection', e);
            this.accessToken = null;
        }
    }

    private refreshAccessToken() {
        if (localStorage.getItem('all-data-access')) {
            this.allDataAccess = JSON.parse(localStorage.getItem('all-data-access'));
            this.accessToken = this.allDataAccess.access_token;
            if (this.allDataAccess?.expires_at_ts && this.allDataAccess?.expires_at_ts?.includes(' ')) {
                // Replace space with "T" to make it ISO-compliant if needed
                const isoDateString = this.allDataAccess.expires_at_ts.replace(' ', 'T') + 'Z'; // "Z" indicates UTC
                this.expireTime = new Date(isoDateString);
            } else {
                this.expireTime = new Date((this.allDataAccess.iat + this.allDataAccess.expires_in) * 1000);
            }
            this.httpHeaders = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.accessToken}`
            });
        }
    }

    hasExpired(): boolean {
        return !(this?.expireTime > new Date());
    }

    private getAccessTokenByUserId(userId: string) {
        return this.httpClient.get(`${Constant.collabticV3ApiUrl}/all-data/access-token?userId=${userId}`);
    }

    getPositionStatements(makeName: string): Observable<any> {
        return this.httpClient.post(`${Constant.collabticV3ApiUrl}/position-statements/get-by-make?makeName=${makeName}`, null, {headers: this.httpHeaders});
    }

    getRuleEngineActions(apiFormData: any): Observable<any> {
        return this.httpClient.post(`${Constant.collabticV3ApiUrl}/all-data/get-rule-engine-actions`, apiFormData, {headers: this.httpHeaders});
    }

    getRoAutoGenConfig(apiFormData: any): Observable<any> {
        return this.httpClient.post(`${Constant.collabticV3ApiUrl}/all-data/ro-auto-gen-config`, apiFormData, {headers: this.httpHeaders});
    }

    getEstimateByReport(apiFormData: any): Observable<any> {
        return this.httpClient.post(`${Constant.collabticV3ApiUrl}/all-data/generate-estimates`, apiFormData, {headers: this.httpHeaders});
    }

    getVinDecodeData(apiFormData: any): Observable<any> {
        return this.httpClient.post(`${Constant.collabticV3ApiUrl}/all-data/get-vin-decode-data`, apiFormData, {headers: this.httpHeaders});
    }

    updateAllDataLinks(apiFormData: any): Observable<any> {
        return this.httpClient.post(`${Constant.collabticV3ApiUrl}/all-data/update-all-data-links`, apiFormData, {headers: this.httpHeaders});
    }

    processAdhocEstimates(apiFormData: any): Observable<any> {
        const url = `${Constant.collabticV3ApiUrl}/ccc-secure/process-adhoc-estimates`;
        // const url = `https://collabtic.local/v1/ccc-secure/process-adhoc-estimates`;

        return this.httpClient.post(url, apiFormData);
    }

    getEstimateAdditionalInfoByRO(apiFormData: any): Observable<any> {
        const url = `${Constant.collabticV3ApiUrl}/ccc-secure/estimate-additional-info`;
        // const url = `https://collabtic.local/v1/ccc-secure/estimate-additional-info`;
        return this.httpClient.post(url, apiFormData, {headers: this.httpHeaders});
    }

    generateEstimateByRO(apiFormData: any): Observable<any> {
        const url = `${Constant.collabticV3ApiUrl}/ccc-secure/generate-estimate-by-ro`;
        // const url = `https://collabtic.local/v1/ccc-secure/generate-estimate-by-ro`;
        return this.httpClient.post(url, apiFormData);
    }

    createReportByEstimate(apiFormData: any): Observable<any> {
        const url = `${Constant.collabticV3ApiUrl}/ccc-secure/create-report-by-estimate`;
        // const url = `https://collabtic.local/v1/ccc-secure/create-report-by-estimate`;
        return this.httpClient.post(url, apiFormData);
    }

    getCCSecureList(apiFormData: any, requestId?: any): Observable<any> {
        const url = `${Constant.collabticV3ApiUrl}/ccc-secure/raw-list`;
        // const url = `https://collabtic.local/v1/ccc-secure/raw-list`;
        return this.httpClient.post(url, apiFormData, {
            headers: new HttpHeaders({
                'Content-Type': 'multipart/form-data'
            })
        });
    }

    getAdasReportList(apiFormData: any): Observable<any> {
        const url = `${Constant.collabticV3ApiUrl}/ccc-secure/list`;
        // const url = `https://collabtic.local/v1/ccc-secure/list`;
        return this.httpClient.post(url, apiFormData, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        });
    }

    getAdasReportTypeCount(apiFormData: any): Observable<any> {
        const url = `${Constant.collabticV3ApiUrl}/ccc-secure/report-type-count`;
        // const url = `https://collabtic.local/v1/ccc-secure/report-type-count`;
        return this.httpClient.post(url, apiFormData, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        });
    }

    estimateDownloadUrl(reportId) {
        return `${Constant.collabticV3ApiUrl}/ccc-secure/estimate-download?reportId=${reportId}`;
    }

    getReportConfigByReportId(userId, domainId): Observable<any> {
        return this.httpClient.get(`${Constant.collabticV3ApiUrl}/ccc-secure/report-config?userId=${userId}&domainId=${domainId}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        });
    }

    updateReportConfig(apiFormData: any): Observable<any> {
        const url = `${Constant.collabticV3ApiUrl}/ccc-secure/update-report-config`;
        // const url = `https://collabtic.local/v1/ccc-secure/update-report-config`;
        return this.httpClient.post(url, apiFormData, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        });
    }

    getDiagnosticsReport(apiFormData: any): Observable<any> {
        const url = `${Constant.collabticV3ApiUrl}/all-data/diagnostics-report`;
        // const url = `https://collabtic.local/v1/all-data/diagnostics-report`;
        return this.httpClient.post(url, apiFormData, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        });
    }

    getAIResponses(reportIdParam: number, isSearch: boolean, allDataInfoParam: any): Observable<any> {
      const url = `${Constant.collabticV3ApiUrl}/ccc-secure/get-ai-responses`;
      return this.httpClient.post(url, {
        reportId: reportIdParam,
        allDataInfo: allDataInfoParam,
        apikey: 'dG9wZml4MTIz',
        userId: localStorage.getItem('userId'),
        domainId: localStorage.getItem('domainId'),
        search: isSearch,
      }, {headers: this.httpHeaders});
    }
}
